import { PlusOutlined } from "@ant-design/icons"
import { Button, Flex, Modal, Skeleton, Typography } from "antd"
import React, { Suspense, lazy, useCallback, useEffect, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"

import { isDeletedAccountRow } from "../../../constants/deleted-account-ui.ts"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import {
  SCROLL_PAGE_SIZE,
  nextPageUrl
} from "../../../redux/shared/paged-list.ts"
import { type RootState } from "../../../store.ts"
import { extractParamsFromNextUrl } from "../../../utils/form/extract-params-from-next-url.ts"
import { handleSaveResult } from "../../../utils/handlers/handle-save-result"
import { serializeData } from "../../../utils/utils"
import {
  CLEARED_CUSTOMER_FILTERS,
  clearCustomersFilters,
  customersSetProp
} from "../redux/customers.slice.ts"
import {
  createCustomer,
  fetchCustomerDetails,
  fetchCustomers,
  fetchCustomersFilterOptions,
  inviteCustomer,
  reactivateCustomer,
  resendInvite,
  suspendCustomer,
  updateCustomer
} from "../redux/customers.thunks.ts"
import { type CustomerFilters } from "../redux/customers.types.ts"
import {
  type Customer,
  type CustomerMutateData,
  type EmailModalState,
  EmailModalStateAction
} from "../types/customer.types.ts"
import { AddEmailModal } from "./AddEmailModal.tsx"
import { CustomerForm } from "./CustomerForm/CustomerForm.tsx"
import { useCallbackRef } from "../../../hooks/use-callback-ref.ts"

const { Text, Title } = Typography

const CustomersListMobile = lazy(
  () => import("./CustomersListMobile/CustomersListMobile.tsx")
)
const CustomerTable = lazy(() => import("./CustomersTable/CustomersTable.tsx"))

const CustomersPage = ({
  resendInvite,
  fetching,
  saveFetching,
  saveResult,
  filterOptions,
  filters,
  result,
  customerPermissions,
  isMobile,
  getCustomers,
  getCustomersFilterOptions,
  clearFilters,
  suspendCustomer,
  reactivateCustomer,
  setProp,
  customerDetailsFetching,
  customerDetails,
  getCustomerDetails,
  createCustomer,
  updateCustomer,
  inviteCustomer
}: ConnectedProps<typeof connector>) => {
  const [showCustomerForm, setShowCustomerForm] = useState<boolean>(false)
  const [emailModalState, setEmailModalState] =
    useState<EmailModalState | null>(null)

  const [page, setPage] = useState<number>(1)
  const [ordering, setOrdering] = useState<string | null>(null)

  const customers = result?.results || []
  const count = result?.count || 0
  const inviteAllowed = customerPermissions?.features?.customer_invite
  const deleteAllowed = customerPermissions?.customers?.customer_delete
  const editAllowed = customerPermissions?.customers?.customer_edit

  const updateData = () => {
    const serializeFilters = serializeData(filters || {})
    const data = {
      // The mobile card list still pages and owns `page`; the desktop table
      // scrolls, and a page left behind would open it partway down a list it
      // cannot scroll back past.
      page: isMobile ? page : 1,
      page_size: SCROLL_PAGE_SIZE,
      ordering,
      ...serializeFilters
    } as CustomerFilters
    getCustomers({ ...data })
  }

  const handleLoadMore = useCallback(() => {
    const url = nextPageUrl(result)
    if (!url) return
    // The server's own url carries the page and every filter it was built
    // with, so nothing here has to reconstruct them.
    getCustomers({
      ...extractParamsFromNextUrl<CustomerFilters>(url),
      isMore: true
    })
  }, [result, getCustomers])

  const handleClickAddCustomer = () => {
    if (customerDetails) setProp({ key: "customerDetails", value: null })
    setShowCustomerForm(true)
  }

  function handleSelectRow(customer: Customer, type: string) {
    if (isDeletedAccountRow(customer)) return

    switch (type) {
      case "invite":
        if (!customer.email) {
          return setEmailModalState({
            customer,
            action: EmailModalStateAction.invite
          })
        }
        return inviteCustomer(customer.id)
      case "edit":
        getCustomerDetails(customer.id)
        setShowCustomerForm(true)
        return
      case "update":
        return updateCustomer(customer)
      case "activate":
        return Modal.confirm({
          centered: true,
          title: "Confirm customer activation",
          content: "Are you sure you want to activate this customer?",
          cancelText: "Cancel",
          okText: "Confirm",
          okButtonProps: { type: "primary" },
          onOk() {
            reactivateCustomer(customer?.id)
          }
        })
      case "suspend": {
        if (!customer?.email) {
          return setEmailModalState({
            customer,
            action: EmailModalStateAction.suspend
          })
        }

        return Modal.confirm({
          centered: true,
          title: "Confirm customer blocking",
          content: "Are you sure you want to block this customer?",
          cancelText: "Cancel",
          okText: "Confirm",
          okButtonProps: { type: "primary" },
          onOk() {
            suspendCustomer(customer?.id)
          }
        })
      }
      case "resend_invite":
        if (!customer.email) {
          return setEmailModalState({
            customer,
            action: EmailModalStateAction.resend_invite
          })
        }
        return resendInvite(customer.id)
    }
  }

  const handleChangeFilter = useCallback(
    (newData: Partial<CustomerFilters>) => {
      setProp({ key: "filters", value: newData })
      setPage(1)
    },
    [setProp]
  )

  const handleChangeSearch = useCallback(
    (value: string | null) => {
      handleChangeFilter({ q: value })
    },
    [handleChangeFilter]
  )

  const handleCreateCustomer = (data: CustomerMutateData) => {
    if (customerDetails) {
      updateCustomer({ ...data, id: customerDetails.id })
    } else {
      createCustomer(data)
    }
  }

  const handleCustomerFormCancel = () => {
    if (customerDetails) setProp({ key: "customerDetails", value: null })
    setShowCustomerForm(false)
  }

  const handleEmailModalSubmit = async (email: string) => {
    if (!emailModalState?.customer) return

    try {
      const id = emailModalState.customer.id
      await updateCustomer({ id, email }).unwrap()

      const actions = {
        [EmailModalStateAction.resend_invite]: resendInvite,
        [EmailModalStateAction.invite]: inviteCustomer,
        [EmailModalStateAction.suspend]: suspendCustomer
      }

      await actions?.[emailModalState?.action]?.(id)
      setEmailModalState(null)
    } catch (e) {
      console.log(e)
    }
  }

  const getCustomersFilterOptionsLatest = useCallbackRef(
    getCustomersFilterOptions
  )
  useEffect(() => {
    generateDocumentTitle("Customers")

    getCustomersFilterOptionsLatest()
  }, [getCustomersFilterOptionsLatest])

  useEffect(() => {
    if (!saveResult) {
      updateData()
    } else {
      if (
        [
          "customer_create",
          "customer_update",
          "customer_delete",
          "customer_invite",
          "customer_suspend",
          "customer_unsuspend"
        ]?.includes(saveResult?.type)
      ) {
        if (saveResult?.message) handleSaveResult(saveResult)

        setProp({ key: "saveResult", value: null })
        setShowCustomerForm(false)

        if (customerDetails) setProp({ key: "customerDetails", value: null })
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- reloads on a change of sort, page, filters or save result; customerDetails is read only to clear it
  }, [ordering, page, filters, saveResult])

  return (
    <div className={"grid size-full"}>
      <Flex vertical gap={"middle"} className={"overflow-auto"}>
        <Flex justify={"space-between"} align={"center"}>
          <Flex vertical>
            <Title className={"text-start"} level={2}>
              Customers
            </Title>
            <Text type={"secondary"}>
              Manage all customers across projects.
            </Text>
          </Flex>
          {(customerPermissions?.customers?.customer_create ||
            customerPermissions?.features?.customer_invite) && (
            <Button
              type={"primary"}
              onClick={handleClickAddCustomer}
              icon={<PlusOutlined />}
            >
              Customer
            </Button>
          )}
        </Flex>

        <Flex className="mb-4" align={"center"} justify={"flex-end"}>
          {showCustomerForm && !customerDetailsFetching && (
            <CustomerForm
              initialValues={customerDetails ?? undefined}
              canInvite={
                customerPermissions?.features?.customer_invite || false
              }
              onSave={handleCreateCustomer}
              onCancel={handleCustomerFormCancel}
            />
          )}
          {emailModalState && (
            <AddEmailModal
              loading={saveFetching}
              onSubmit={handleEmailModalSubmit}
              action={emailModalState?.action}
              onCancel={() => setEmailModalState(null)}
            />
          )}
        </Flex>
        <Suspense fallback={<Skeleton.Node active className={"!size-full"} />}>
          {isMobile ? (
            <CustomersListMobile
              customers={customers}
              count={count}
              page={page}
              pageSize={SCROLL_PAGE_SIZE}
              filters={filters}
              filterOptions={filterOptions}
              loading={fetching || saveFetching}
              inviteAllowed={inviteAllowed}
              deleteAllowed={deleteAllowed}
              editAllowed={editAllowed}
              onPageChange={setPage}
              onFiltersChange={handleChangeFilter}
              onSearchChange={handleChangeSearch}
              onSelectRow={handleSelectRow}
              clearFilters={clearFilters}
            />
          ) : (
            <CustomerTable
              customers={customers}
              count={count}
              next={result?.next}
              retryNext={result?.retryNext}
              loadFailed={result?.loadFailed}
              onRetry={updateData}
              onClearFilters={() =>
                handleChangeFilter(CLEARED_CUSTOMER_FILTERS)
              }
              filters={filters}
              filterOptions={filterOptions}
              loading={fetching || saveFetching}
              inviteAllowed={inviteAllowed}
              deleteAllowed={deleteAllowed}
              editAllowed={editAllowed}
              onLoadMore={handleLoadMore}
              onOrderingChange={setOrdering}
              onFilterChange={handleChangeFilter}
              onSearchChange={handleChangeSearch}
              onSelectRow={handleSelectRow}
            />
          )}
        </Suspense>
      </Flex>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  const customers = state.customers

  return {
    fetching: customers.fetching,
    result: customers.result,
    customerDetailsFetching: customers.customerDetailsFetching,
    customerDetails: customers.customerDetails,

    filters: customers.filters,
    filterOptions: customers.filterOptions,

    saveFetching: customers.saveFetching,
    saveResult: customers.saveResult,
    customerPermissions: state?.auth?.permissions?.codes?.customers,
    isMobile: state.nav.isMobile
  }
}

const mapDispatchToProps = {
  createCustomer: createCustomer,
  updateCustomer: updateCustomer,
  getCustomerDetails: fetchCustomerDetails,
  getCustomersFilterOptions: fetchCustomersFilterOptions,
  getCustomers: fetchCustomers,
  resendInvite: resendInvite,
  suspendCustomer: suspendCustomer,
  reactivateCustomer: reactivateCustomer,
  inviteCustomer: inviteCustomer,
  setProp: customersSetProp,
  clearFilters: clearCustomersFilters
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(CustomersPage)
